import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { waitFor } from 'xstate';

import {
  useAppointmentStartedAt,
  useUpdateAppointmentStatus,
} from '@eluve/frontend-appointment-hooks';
import { AppointmentStatusTypesLookup } from '@eluve/graphql-types';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useSetAppointmentStartedAt } from '@eluve/smart-blocks';

import { useTranscriptionMachineActor } from '../hooks/useTranscriptionMachine';

/**
 * Returns a function that can be used to start a recording for an appointment.
 * This takes care of acquiring the microphone and updating the appointment status
 * and startedAt as needed.
 */
export const useStartAppointmentRecording = ({
  tenantId,
  appointmentId,
  selectedMicId,
}: {
  tenantId: string;
  appointmentId: string;
  selectedMicId?: string | null;
}) => {
  const [isStartingAppointmentRecording, setIsStartingAppointmentRecording] =
    useState(false);
  const transcriptionMachine = useTranscriptionMachineActor();

  const appointmentStartedAt = useAppointmentStartedAt();

  const updateAppointmentStatus = useUpdateAppointmentStatus();
  const setAppointmentStartedAt = useSetAppointmentStartedAt();
  const captureEvent = useCaptureEvent({
    appointmentId,
  });

  return {
    isStartingAppointmentRecording,
    validateAudioCapture: () => {
      transcriptionMachine.send({
        type: 'AUDIO_VALIDATION_REQUESTED',
        deviceId: selectedMicId ?? undefined,
      });
    },
    startAppointmentRecording: async (args?: { playDisclaimer?: boolean }) => {
      if (isStartingAppointmentRecording) {
        return;
      }

      try {
        setIsStartingAppointmentRecording(true);

        const { playDisclaimer } = args ?? {};
        const microphoneName = navigator.mediaDevices
          ? (await navigator.mediaDevices.enumerateDevices()).find(
              (device) => device.deviceId === selectedMicId,
            )?.label
          : undefined;

        if (appointmentStartedAt === null) {
          captureEvent('started_session', { microphoneName });
        } else {
          captureEvent('restarted_session', { microphoneName });
        }

        transcriptionMachine.send({
          type: 'START',
          appointmentId,
          microphoneName,
          playDisclaimer,
          deviceId: selectedMicId ?? undefined,
        });

        await waitFor(
          transcriptionMachine,
          (state) =>
            state.matches({ InSession: 'Recording' }) &&
            state.context.sessionAppointmentId === appointmentId,
        );

        if (appointmentStartedAt === null) {
          await setAppointmentStartedAt({ variables: { id: appointmentId } });
        } else {
          await updateAppointmentStatus({
            variables: {
              tenantId,
              appointmentId,
              status: AppointmentStatusTypesLookup.ACTIVE,
            },
          });
        }
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        setIsStartingAppointmentRecording(false);
      }
    },
  };
};
